/**
 * EventFormBuilder
 * ================
 * Drag-and-drop registration form builder for an event. Lives on top of the
 * existing `/api/admin/events/{event_id}/custom-fields` CRUD so we don't
 * fragment data — the public registration page already reads the same
 * collection and will pick up changes automatically.
 *
 * Phase A scope (today):
 *   • 22 field types (text/paragraph/number/email/phone/date/time/dropdown/
 *     radio/checkbox/yes-no/country/province/church/assembly/campus/file/
 *     photo/id_upload/signature/hidden/custom)
 *   • Required toggle + label / help text / placeholder / default value
 *   • Min/max length + min/max value + regex validation
 *   • Drag-to-reorder using @dnd-kit
 *   • Show-on-badge flag + price trigger (stored now, wired in Phase B)
 *   • "Visible when" conditional rule editor (raw JSON, surfaced in UI Phase B)
 *   • Duplicate form from another event (template reuse)
 *
 * What's NOT in Phase A (intentional):
 *   • Conditional-display *execution* on the public form (UI builds the rule,
 *     renderer ignores it for now — that's Phase B)
 *   • File / signature / photo / ID upload rendering (Phase C)
 *   • Per-field payment trigger execution (Phase D)
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  GripVertical, Trash2, Plus, Loader2, Eye, Copy, Settings2,
} from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const apiFetch = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.detail || `Request failed (${res.status})`);
  }
  return res.json();
};

const FIELD_TYPE_HINTS = {
  // Maps the catalogue from the backend meta endpoint to a one-line UI hint.
  text: 'Short text input',
  paragraph: 'Multi-line textarea',
  number: 'Numeric value',
  email: 'Email address (validated)',
  phone: 'Phone number',
  date: 'Date picker',
  time: 'Time picker',
  dropdown: 'Single-select dropdown',
  single_choice: 'Radio group (pick one)',
  multi_choice: 'Checkbox group (pick many)',
  yes_no: 'Yes / No toggle',
  country: 'Country picker (lookup)',
  province: 'Province / region picker (lookup)',
  church: 'Church picker (lookup)',
  assembly: 'Assembly picker (lookup)',
  campus: 'Campus picker (lookup)',
  file: 'File upload',
  photo: 'Photo upload',
  id_upload: 'ID document upload',
  signature: 'Signature pad',
  hidden: 'Hidden value (passed silently)',
};

/** A single sortable row in the field list. */
const SortableFieldRow = ({ field, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: field.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 px-3 py-2 bg-white border rounded-md hover:border-emerald-300"
      data-testid={`fb-field-row-${field.id}`}
    >
      <button
        type="button"
        className="text-slate-400 hover:text-slate-700 cursor-grab"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium truncate text-slate-800">{field.label}</span>
          {field.required && <Badge variant="destructive" className="text-[10px]">Required</Badge>}
          {!field.is_active && <Badge variant="secondary" className="text-[10px]">Inactive</Badge>}
          {field.show_on_badge && <Badge className="text-[10px] bg-amber-100 text-amber-800">Badge</Badge>}
          {field.price_amount > 0 && (
            <Badge className="text-[10px] bg-emerald-100 text-emerald-800">
              +{field.price_currency || ''}{field.price_amount}
            </Badge>
          )}
        </div>
        <p className="text-[10px] text-slate-400 font-mono truncate">
          {field.field_type} · key={field.field_key || '—'}
        </p>
      </div>
      <Button size="sm" variant="ghost" onClick={() => onEdit(field)} data-testid={`fb-edit-${field.id}`}>
        <Settings2 className="h-4 w-4" />
      </Button>
      <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700"
        onClick={() => onDelete(field)} data-testid={`fb-delete-${field.id}`}>
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
};

export const EventFormBuilder = ({ eventId }) => {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showDuplicate, setShowDuplicate] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const { data: meta } = useQuery({
    queryKey: ['fb-meta'], queryFn: () => apiFetch('/api/admin/custom-fields/meta'),
    staleTime: 60_000,
  });
  const { data: fields = [], isLoading } = useQuery({
    queryKey: ['fb-fields', eventId],
    queryFn: () => apiFetch(`/api/admin/events/${eventId}/custom-fields`),
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const reorderMut = useMutation({
    mutationFn: (orderedIds) => apiFetch(
      `/api/admin/events/${eventId}/custom-fields-reorder`,
      { method: 'PUT', body: JSON.stringify({ field_ids: orderedIds }) },
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fb-fields', eventId] }),
    onError: (e) => toast.error(e.message),
  });
  const delMut = useMutation({
    mutationFn: (id) => apiFetch(`/api/admin/events/${eventId}/custom-fields/${id}`,
      { method: 'DELETE' }),
    onSuccess: () => { toast.success('Field deleted'); qc.invalidateQueries({ queryKey: ['fb-fields', eventId] }); },
    onError: (e) => toast.error(e.message),
  });

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = fields.findIndex(f => f.id === active.id);
    const newIndex = fields.findIndex(f => f.id === over.id);
    const reordered = arrayMove(fields, oldIndex, newIndex);
    // Optimistically update cache while the server is catching up.
    qc.setQueryData(['fb-fields', eventId], reordered);
    reorderMut.mutate(reordered.map(f => f.id));
  };

  return (
    <div className="space-y-4" data-testid="event-form-builder">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Registration form fields</h2>
          <p className="text-xs text-slate-500">Drag to reorder. Changes apply to the public registration form immediately.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowPreview(true)} data-testid="fb-preview-btn">
            <Eye className="h-4 w-4 mr-1" /> Preview
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowDuplicate(true)} data-testid="fb-duplicate-btn">
            <Copy className="h-4 w-4 mr-1" /> Duplicate from…
          </Button>
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="fb-add-field-btn">
            <Plus className="h-4 w-4 mr-1" /> Add field
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-3">
          {isLoading ? (
            <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
          ) : fields.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400" data-testid="fb-empty">
              No registration fields yet. The form will collect the default name / email / phone fields only.
              Click "Add field" to start customising.
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2" data-testid="fb-field-list">
                  {fields.map(f => (
                    <SortableFieldRow
                      key={f.id}
                      field={f}
                      onEdit={setEditing}
                      onDelete={(f) => {
                        if (window.confirm(`Delete the "${f.label}" field?`)) delMut.mutate(f.id);
                      }}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </CardContent>
      </Card>

      {showAdd && (
        <FieldEditor
          eventId={eventId}
          meta={meta}
          existing={null}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); qc.invalidateQueries({ queryKey: ['fb-fields', eventId] }); }}
        />
      )}
      {editing && (
        <FieldEditor
          eventId={eventId}
          meta={meta}
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['fb-fields', eventId] }); }}
        />
      )}
      {showDuplicate && (
        <DuplicateFormDialog
          eventId={eventId}
          onClose={() => setShowDuplicate(false)}
          onDone={() => { setShowDuplicate(false); qc.invalidateQueries({ queryKey: ['fb-fields', eventId] }); }}
        />
      )}
      {showPreview && (
        <PreviewDialog eventId={eventId} fields={fields} onClose={() => setShowPreview(false)} />
      )}
    </div>
  );
};

/* ────────────────────── Field editor ────────────────────── */

const emptyField = {
  label: '', field_type: 'text', field_key: '', required: false,
  placeholder: '', help_text: '', default_value: '', options: [],
  is_active: true, min_length: null, max_length: null,
  min_value: null, max_value: null, pattern: '', visible_when: null,
  show_on_badge: false, price_amount: null, price_currency: '',
};

const FieldEditor = ({ eventId, meta, existing, onClose, onSaved }) => {
  const isNew = !existing;
  const [form, setForm] = useState({ ...emptyField, ...(existing || {}) });

  const typeInfo = (meta?.types || []).find(t => t.key === form.field_type);

  const saveMut = useMutation({
    mutationFn: () => apiFetch(
      isNew
        ? `/api/admin/events/${eventId}/custom-fields`
        : `/api/admin/events/${eventId}/custom-fields/${existing.id}`,
      { method: isNew ? 'POST' : 'PUT', body: JSON.stringify(form) },
    ),
    onSuccess: () => { toast.success(isNew ? 'Field added' : 'Field updated'); onSaved?.(); },
    onError: (e) => toast.error(e.message),
  });

  const setOpt = (i, k, v) => setForm(f => ({
    ...f,
    options: (f.options || []).map((o, j) => j === i ? { ...o, [k]: v } : o),
  }));
  const addOpt = () => setForm(f => ({ ...f, options: [...(f.options || []), { label: '', value: '' }] }));
  const delOpt = (i) => setForm(f => ({ ...f, options: (f.options || []).filter((_, j) => j !== i) }));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="fb-field-editor">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Add a field' : `Edit · ${existing.label}`}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Field type</Label>
            <Select value={form.field_type} onValueChange={v => setForm(f => ({ ...f, field_type: v }))}>
              <SelectTrigger data-testid="fb-field-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(meta?.types || []).map(t => (
                  <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-400 mt-1">{FIELD_TYPE_HINTS[form.field_type] || ''}</p>
          </div>
          <div className="col-span-2">
            <Label>Label *</Label>
            <Input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} data-testid="fb-field-label" />
          </div>
          <div>
            <Label>Field key</Label>
            <Input
              value={form.field_key}
              onChange={e => setForm(f => ({ ...f, field_key: e.target.value }))}
              placeholder="auto-derived from label"
              data-testid="fb-field-key"
            />
            <p className="text-[10px] text-slate-400 mt-1">Machine name used in CSV exports and the answer dict.</p>
          </div>
          <div>
            <Label>Display order</Label>
            <Input type="number" value={form.order || ''} onChange={e => setForm(f => ({ ...f, order: parseInt(e.target.value) || 0 }))} />
          </div>
          <div className="col-span-2">
            <Label>Placeholder</Label>
            <Input value={form.placeholder} onChange={e => setForm(f => ({ ...f, placeholder: e.target.value }))} />
          </div>
          <div className="col-span-2">
            <Label>Help text</Label>
            <Input value={form.help_text} onChange={e => setForm(f => ({ ...f, help_text: e.target.value }))} />
          </div>
          <div className="col-span-2">
            <Label>Default value</Label>
            <Input value={form.default_value || ''} onChange={e => setForm(f => ({ ...f, default_value: e.target.value }))} />
          </div>

          {/* Options for choice-type fields */}
          {typeInfo?.has_options && (
            <div className="col-span-2 border-t pt-3">
              <Label>Options</Label>
              <div className="space-y-2 mt-2">
                {(form.options || []).map((o, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      placeholder="Label" value={o.label}
                      onChange={e => setOpt(i, 'label', e.target.value)}
                      data-testid={`fb-opt-label-${i}`}
                    />
                    <Input
                      placeholder="Value" value={o.value}
                      onChange={e => setOpt(i, 'value', e.target.value)}
                      data-testid={`fb-opt-value-${i}`}
                    />
                    <Button variant="ghost" size="icon" className="text-red-600" onClick={() => delOpt(i)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={addOpt} data-testid="fb-add-opt">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add option
                </Button>
              </div>
            </div>
          )}

          {/* Validation block */}
          <div className="col-span-2 border-t pt-3">
            <Label>Validation</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div>
                <Label className="text-[10px]">Min length</Label>
                <Input type="number" value={form.min_length ?? ''} onChange={e => setForm(f => ({ ...f, min_length: e.target.value === '' ? null : parseInt(e.target.value) }))} />
              </div>
              <div>
                <Label className="text-[10px]">Max length</Label>
                <Input type="number" value={form.max_length ?? ''} onChange={e => setForm(f => ({ ...f, max_length: e.target.value === '' ? null : parseInt(e.target.value) }))} />
              </div>
              <div>
                <Label className="text-[10px]">Min value</Label>
                <Input type="number" value={form.min_value ?? ''} onChange={e => setForm(f => ({ ...f, min_value: e.target.value === '' ? null : parseFloat(e.target.value) }))} />
              </div>
              <div>
                <Label className="text-[10px]">Max value</Label>
                <Input type="number" value={form.max_value ?? ''} onChange={e => setForm(f => ({ ...f, max_value: e.target.value === '' ? null : parseFloat(e.target.value) }))} />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px]">Regex pattern</Label>
                <Input value={form.pattern || ''} onChange={e => setForm(f => ({ ...f, pattern: e.target.value }))} placeholder="^[A-Z]{2}[0-9]{6}$" />
              </div>
            </div>
          </div>

          {/* Toggles */}
          <div className="col-span-2 border-t pt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.required} onChange={e => setForm(f => ({ ...f, required: e.target.checked }))} data-testid="fb-required" />
              Required
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
              Active
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.show_on_badge} onChange={e => setForm(f => ({ ...f, show_on_badge: e.target.checked }))} data-testid="fb-show-on-badge" />
              Show on badge
            </label>
          </div>

          {/* Payment trigger */}
          <div className="col-span-2 border-t pt-3">
            <Label>Payment trigger (optional)</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <Input type="number" placeholder="Amount (e.g. 50)" value={form.price_amount ?? ''} onChange={e => setForm(f => ({ ...f, price_amount: e.target.value === '' ? null : parseFloat(e.target.value) }))} />
              <Input placeholder="Currency (e.g. USD)" value={form.price_currency || ''} onChange={e => setForm(f => ({ ...f, price_currency: e.target.value.toUpperCase() }))} />
            </div>
            <p className="text-[10px] text-slate-400 mt-1">When set, picking this field's "yes" / non-empty value will add this amount to the registration total. Wired in Phase D.</p>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          <Button onClick={() => {
            if (!form.label) return toast.error('Label is required');
            saveMut.mutate();
          }} disabled={saveMut.isPending} data-testid="fb-save-field-btn">
            {saveMut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            {isNew ? 'Add field' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ────────────────────── Duplicate dialog ────────────────────── */

const DuplicateFormDialog = ({ eventId, onClose, onDone }) => {
  const [srcId, setSrcId] = useState('');
  const dup = useMutation({
    mutationFn: () => apiFetch(
      `/api/admin/events/${eventId}/custom-fields/duplicate-from/${srcId}`,
      { method: 'POST' },
    ),
    onSuccess: (d) => { toast.success(`Copied ${d.copied} fields`); onDone?.(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Duplicate form from another event</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label>Source event ID</Label>
          <Input value={srcId} onChange={e => setSrcId(e.target.value)} placeholder="Paste the event id" />
          <p className="text-[10px] text-slate-400">All fields from that event will be copied to this one. Existing fields are preserved.</p>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          <Button onClick={() => dup.mutate()} disabled={!srcId || dup.isPending}>
            {dup.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Copy fields
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ────────────────────── Preview dialog ────────────────────── */

const PreviewDialog = ({ eventId, fields, onClose }) => {
  // Rough preview of how the form will render. Renders read-only inputs
  // styled like the public form. Not interactive — that's what the public
  // registration page is for.
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto" data-testid="fb-preview-dialog">
        <DialogHeader>
          <DialogTitle>Form preview</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {fields.length === 0 && <p className="text-sm text-slate-400">No fields to preview yet.</p>}
          {fields.filter(f => f.is_active && f.field_type !== 'hidden').map(f => (
            <div key={f.id}>
              <Label>{f.label}{f.required ? ' *' : ''}</Label>
              {renderPreviewInput(f)}
              {f.help_text && <p className="text-[10px] text-slate-400 mt-1">{f.help_text}</p>}
            </div>
          ))}
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Close</Button></DialogClose>
          <Button asChild>
            <a href={`/events/${eventId}`} target="_blank" rel="noreferrer">Open live form</a>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

function renderPreviewInput(f) {
  const placeholder = f.placeholder || '';
  switch (f.field_type) {
    case 'paragraph':
      return <Textarea placeholder={placeholder} disabled rows={3} />;
    case 'number':
      return <Input type="number" placeholder={placeholder} disabled />;
    case 'email':
      return <Input type="email" placeholder={placeholder} disabled />;
    case 'phone':
      return <Input type="tel" placeholder={placeholder} disabled />;
    case 'date':
      return <Input type="date" disabled />;
    case 'time':
      return <Input type="time" disabled />;
    case 'dropdown':
      return (
        <select disabled className="w-full px-3 py-2 border rounded-md text-sm bg-slate-50">
          <option>{placeholder || 'Choose…'}</option>
          {(f.options || []).map((o, i) => <option key={i}>{o.label}</option>)}
        </select>
      );
    case 'single_choice':
      return <div className="space-y-1">
        {(f.options || []).map((o, i) => <label key={i} className="flex gap-2 text-sm"><input type="radio" disabled /> {o.label}</label>)}
      </div>;
    case 'multi_choice':
      return <div className="space-y-1">
        {(f.options || []).map((o, i) => <label key={i} className="flex gap-2 text-sm"><input type="checkbox" disabled /> {o.label}</label>)}
      </div>;
    case 'yes_no':
      return <div className="flex gap-3 text-sm">
        <label className="flex gap-1"><input type="radio" disabled /> Yes</label>
        <label className="flex gap-1"><input type="radio" disabled /> No</label>
      </div>;
    case 'file':
    case 'photo':
    case 'id_upload':
      return <Input type="file" disabled />;
    case 'signature':
      return <div className="h-16 bg-slate-100 border border-dashed rounded-md text-center text-xs text-slate-400 grid place-items-center">Signature pad (renders in Phase C)</div>;
    case 'hidden':
      return <p className="text-[10px] text-slate-400">Hidden — not visible to user.</p>;
    default:
      return <Input placeholder={placeholder} disabled />;
  }
}

export default EventFormBuilder;
